import { loadGoogleMaps } from '@theme/delivery-location-maps';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {Object} DeliveryLocationData
 * @property {string} address
 * @property {number} lat
 * @property {number} lng
 * @property {string} mapsUrl
 * @property {string} placeId
 * @property {string} source - "Current Location" or "Search"
 * @property {string} selectedAt - ISO timestamp
 */

/**
 * <delivery-location-modal>
 *
 * Singleton, store-wide address picker rendered once from `layout/theme.liquid`
 * (see `snippets/delivery-location-modal.liquid`) — never per-page, never
 * per-product. Lets a shopper pick a delivery address via Google Places
 * Autocomplete, browser geolocation + reverse geocoding, or by dragging a map
 * marker. The selection is persisted in Local or Session Storage under a
 * single key and synced onto the cart as one consolidated JSON order-level
 * attribute (see `syncCartAttribute`) — the single authoritative copy of the
 * address for checkout and fulfillment, rather than a per-line-item property.
 *
 * The modal never opens on its own on page load/navigation. It only opens:
 *   1) On demand via `requestSelection()`, called by
 *      `@theme/delivery-location-service`'s `ensureDeliveryLocation()` when
 *      an Add to Cart action needs a location that isn't set yet. Callers
 *      await the returned Promise and resume their own cart/add.js request
 *      once it resolves `true`.
 *   2) Explicitly via any element with `[data-delivery-location-trigger]`
 *      anywhere in the document (e.g. the header trigger/status control) —
 *      an unconditional open used to let shoppers change their location.
 */
class DeliveryLocationModal extends DialogComponent {
  constructor() {
    super();
    /** @type {DeliveryLocationData | null} */
    this.pendingSelection = null;
    /** @type {DeliveryLocationData | null} */
    this.savedSelection = null;
    /** @type {google.maps.Map | null} */
    this.map = null;
    /** @type {google.maps.Marker | null} */
    this.marker = null;
    /** @type {google.maps.Geocoder | null} */
    this.geocoder = null;
    /** @type {google.maps.places.PlaceAutocompleteElement | null} */
    this.autocomplete = null;
    /** @type {Promise<typeof google.maps> | null} */
    this.mapsLoadPromise = null;

    /** @type {HTMLDialogElement | null} */
    this.dialog = null;
    /** @type {HTMLElement | null} */
    this.searchWrapper = null;
    /** @type {HTMLButtonElement | null} */
    this.currentLocationButton = null;
    /** @type {HTMLElement | null} */
    this.mapContainer = null;
    /** @type {HTMLButtonElement | null} */
    this.proceedButton = null;
    /** @type {HTMLElement | null} */
    this.statusRegion = null;

    /** @type {string} */
    this.apiKey = '';
    /** @type {'localStorage' | 'sessionStorage'} */
    this.storageType = 'localStorage';
    /** @type {boolean} */
    this.closeOnEsc = true;
    /** @type {boolean} */
    this.closeOnOverlay = true;
    /** @type {boolean} */
    this.enableAutocomplete = true;
    /** @type {boolean} */
    this.enableCurrentLocation = true;
    /** @type {boolean} */
    this.enableReverseGeocoding = true;
    /** @type {boolean} */
    this.enableDraggableMarker = true;
    /** @type {number} */
    this.defaultLat = 0;
    /** @type {number} */
    this.defaultLng = 0;
    /** @type {number} */
    this.defaultZoom = 12;

    /** @type {Array<(value: boolean) => void>} */
    this.pendingResolvers = [];

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleProceedClick = this.handleProceedClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleCurrentLocationClick = this.handleCurrentLocationClick.bind(this);
    this.handlePlaceSelect = this.handlePlaceSelect.bind(this);
    this.handleDialogClose = this.handleDialogClose.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.dialog = /** @type {HTMLDialogElement} */ (this.refs.dialog);
    this.searchWrapper = this.querySelector('[data-delivery-location-search-wrapper]');
    this.currentLocationButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-delivery-location-current]')
    );
    this.mapContainer = this.querySelector('[data-delivery-location-map]');
    this.proceedButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-delivery-location-proceed]')
    );
    this.statusRegion = this.querySelector('[data-delivery-location-status]');

    this.apiKey = this.dataset.apiKey || '';
    this.storageType = this.dataset.storageType === 'session' ? 'sessionStorage' : 'localStorage';
    this.closeOnEsc = this.dataset.closeOnEsc !== 'false';
    this.closeOnOverlay = this.dataset.closeOnOverlay !== 'false';

    if (!this.closeOnEsc || !this.closeOnOverlay) {
      this.addEventListener('keydown', this.guardEscapeClose.bind(this), { capture: true });
      this.addEventListener('click', this.guardOverlayClose.bind(this), { capture: true });
    }

    this.enableAutocomplete = this.dataset.enableAutocomplete !== 'false';
    this.enableCurrentLocation = this.dataset.enableCurrentLocation !== 'false';
    this.enableReverseGeocoding = this.dataset.enableReverseGeocoding !== 'false';
    this.enableDraggableMarker = this.dataset.enableDraggableMarker !== 'false';
    this.defaultLat = Number(this.dataset.defaultLat) || 0;
    this.defaultLng = Number(this.dataset.defaultLng) || 0;
    this.defaultZoom = Number(this.dataset.defaultZoom) || 12;

    this.proceedButton?.addEventListener('click', this.handleProceedClick);
    for (const cancelButton of this.querySelectorAll('[data-delivery-location-cancel]')) {
      cancelButton.addEventListener('click', this.handleCloseClick);
    }
    this.currentLocationButton?.addEventListener('click', this.handleCurrentLocationClick);
    this.addEventListener(DialogCloseEvent.eventName, this.handleDialogClose);

    // Header trigger and any other explicit "change location" control live
    // outside this element's own subtree, so this listens document-wide.
    document.addEventListener('click', this.handleTriggerClick);

    this.restoreSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleTriggerClick);
  }

  /**
   * Pauses an Add to Cart request until the shopper picks a location.
   * Multiple concurrent callers (e.g. two different products' Add to Cart
   * clicked in quick succession) share the same pending request — a single
   * Proceed resolves all of them; a single Cancel/Escape/outside-click
   * resolves all of them `false`.
   * @returns {Promise<boolean>}
   */
  requestSelection() {
    const promise = new Promise((resolve) => {
      this.pendingResolvers.push(resolve);
    });

    if (!this.dialog?.open) {
      this.openModal();
    }

    return promise;
  }

  /**
   * @param {boolean} value
   */
  resolvePending(value) {
    if (!this.pendingResolvers.length) return;

    const resolvers = this.pendingResolvers;
    this.pendingResolvers = [];
    for (const resolve of resolvers) resolve(value);
  }

  handleDialogClose() {
    // If the dialog closed without Proceed having run, any pending
    // Add to Cart requests are abandoned together.
    this.resolvePending(false);
  }

  /**
   * @returns {boolean}
   */
  hasSelection() {
    return Boolean(this.savedSelection?.address);
  }

  /**
   * Shared across every product in the store — set once, applies to any
   * Add to Cart action anywhere in the theme until the shopper explicitly
   * changes it (e.g. via the header trigger).
   */
  get storageKey() {
    return 'delivery-location:global';
  }

  /**
   * Restores a previously saved selection, if any, and reflects it in the
   * header trigger/status and cart attributes. Runs on every page load —
   * this never opens the modal itself, it only restores/syncs state.
   */
  restoreSelection() {
    try {
      const raw = window[this.storageType].getItem(this.storageKey);
      if (!raw) return;

      const data = JSON.parse(raw);
      if (!data || typeof data.address !== 'string') return;

      this.savedSelection = data;
      this.updateHeaderLabel(data);
      this.syncCartAttribute(data);
    } catch {
      // Corrupted or inaccessible storage — treat as no saved selection.
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleTriggerClick(event) {
    const target = /** @type {Element | null} */ (event.target);
    if (!target?.closest('[data-delivery-location-trigger]')) return;

    event.preventDefault();
    this.openModal();
  }

  /**
   * @param {MouseEvent} event
   */
  handleCloseClick(event) {
    event.preventDefault();
    this.closeDialog();
  }

  /**
   * @param {KeyboardEvent} event
   */
  guardEscapeClose(event) {
    if (!this.closeOnEsc && event.key === 'Escape') {
      event.stopImmediatePropagation();
    }
  }

  /**
   * @param {MouseEvent} event
   */
  guardOverlayClose(event) {
    if (!this.closeOnOverlay && this.dialog && event.target === this.dialog) {
      event.stopImmediatePropagation();
    }
  }

  async openModal() {
    // Every open starts from a clean *pending* slate — otherwise a stale
    // `pendingSelection` left over from a PREVIOUS open (the shopper opened
    // the modal, picked an address, closed it, then reopened it later to
    // change their mind) could still satisfy `updateProceedState()` and get
    // re-saved by a Proceed click before the shopper's new search/current-
    // location/map interaction has actually produced a new selection —
    // which looks exactly like "I picked a new location but the old one
    // won kept being saved."
    //
    // But the shopper's already-*saved* selection (if any) is restored right
    // back as the starting pending state below, via `setPendingSelection`.
    // Without that, reopening to change an address left the search box
    // empty and Proceed disabled until a brand new address was picked —
    // reading as "my location got cleared" even though it was still saved
    // and still applied to the cart.
    this.pendingSelection = null;
    this.updateProceedState();

    this.showDialog();
    this.setStatus('');

    if (!this.mapsReady) {
      await this.ensureMapsLoaded();
    }

    if (this.savedSelection) {
      this.setPendingSelection(this.savedSelection, { skipMapUpdate: !this.mapsReady });
    } else if (this.autocomplete) {
      this.autocomplete.value = '';
    }
  }

  get mapsReady() {
    return Boolean(this.map && this.geocoder);
  }

  async ensureMapsLoaded() {
    if (!this.apiKey) {
      this.setStatus(this.translations.errorApiKeyMissing, true);
      return;
    }

    if (!this.mapsLoadPromise) {
      this.mapsLoadPromise = loadGoogleMaps(this.apiKey);
    }

    try {
      await this.mapsLoadPromise;
      this.initMap();
      await this.initAutocomplete();
    } catch {
      this.setStatus(this.translations.errorApiLoadFailed, true);
    }
  }

  initMap() {
    if (this.map || !this.mapContainer || !window.google) return;

    const startLat = this.savedSelection?.lat ?? this.defaultLat;
    const startLng = this.savedSelection?.lng ?? this.defaultLng;
    const center = { lat: startLat, lng: startLng };

    this.map = new google.maps.Map(this.mapContainer, {
      center,
      zoom: this.defaultZoom,
      disableDefaultUI: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    });

    this.geocoder = new google.maps.Geocoder();

    this.marker = new google.maps.Marker({
      position: center,
      map: this.map,
      draggable: this.enableDraggableMarker,
    });

    if (this.enableDraggableMarker) {
      this.marker.addListener('dragend', () => {
        const position = this.marker.getPosition();
        if (position) this.reverseGeocode(position.lat(), position.lng());
      });
    }

    this.map.addListener('click', (/** @type {google.maps.MapMouseEvent} */ event) => {
      if (!this.enableDraggableMarker || !event.latLng) return;
      this.marker.setPosition(event.latLng);
      this.reverseGeocode(event.latLng.lat(), event.latLng.lng());
    });

    if (this.savedSelection) {
      this.setPendingSelection(this.savedSelection, { skipMapUpdate: true });
    }
  }

  /**
   * Creates and inserts a `PlaceAutocompleteElement` — the current Google-supported
   * autocomplete widget. The legacy `google.maps.places.Autocomplete` class used
   * previously is not available to Google Cloud projects created after March 1,
   * 2025, so it renders an input but never returns suggestions on newer API keys.
   */
  async initAutocomplete() {
    if (this.autocomplete || !this.enableAutocomplete || !this.searchWrapper || !window.google) return;

    const { PlaceAutocompleteElement } = /** @type {any} */ (
      await google.maps.importLibrary('places')
    );

    this.autocomplete = new PlaceAutocompleteElement({
      includedRegionCodes: ['in'],
    });
    this.autocomplete.placeholder = this.searchWrapper.dataset.placeholder || '';
    this.autocomplete.id = this.searchWrapper.dataset.inputId || '';

    this.searchWrapper.replaceChildren(this.autocomplete);

    this.autocomplete.addEventListener('gmp-select', this.handlePlaceSelect);
    this.autocomplete.addEventListener('gmp-error', () => {
      this.setStatus(this.translations.errorGeocodeFailed, true);
    });
  }

  /**
   * @param {{placePrediction: google.maps.places.PlacePrediction}} event
   */
  async handlePlaceSelect({ placePrediction }) {
    if (!placePrediction) return;

    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({ fields: ['id', 'formattedAddress', 'location'] });

      if (!place.location) {
        this.setStatus(this.translations.errorInvalidAddress, true);
        return;
      }

      const lat = place.location.lat();
      const lng = place.location.lng();

      this.setPendingSelection({
        address: place.formattedAddress || '',
        lat,
        lng,
        placeId: place.id || '',
        source: this.translations.sourceSearch,
      });

      this.centerMap(lat, lng);
    } catch {
      this.setStatus(this.translations.errorGeocodeFailed, true);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleCurrentLocationClick(event) {
    event.preventDefault();

    if (!this.enableCurrentLocation || !this.currentLocationButton) return;

    if (!navigator.geolocation) {
      this.setStatus(this.translations.errorGeolocationUnsupported, true);
      return;
    }

    this.setStatus(this.translations.detectingLocation);
    this.currentLocationButton.disabled = true;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        if (this.currentLocationButton) this.currentLocationButton.disabled = false;
        const { latitude, longitude } = position.coords;

        if (!this.mapsReady) await this.ensureMapsLoaded();

        this.centerMap(latitude, longitude);
        this.reverseGeocode(latitude, longitude, this.translations.sourceCurrentLocation);
      },
      (error) => {
        if (this.currentLocationButton) this.currentLocationButton.disabled = false;

        if (error.code === error.PERMISSION_DENIED) {
          this.setStatus(this.translations.errorPermissionDenied, true);
        } else if (error.code === error.TIMEOUT) {
          this.setStatus(this.translations.errorTimeout, true);
        } else {
          this.setStatus(this.translations.errorPositionUnavailable, true);
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }

  /**
   * @param {number} lat
   * @param {number} lng
   * @param {string} [source]
   */
  reverseGeocode(lat, lng, source) {
    if (!this.enableReverseGeocoding || !this.geocoder) {
      this.setPendingSelection({ address: '', lat, lng, placeId: '', source: source || this.translations.sourceSearch });
      return;
    }

    this.geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        this.setStatus(this.translations.errorGeocodeFailed, true);
        return;
      }

      this.setPendingSelection({
        address: results[0].formatted_address,
        lat,
        lng,
        placeId: results[0].place_id || '',
        source: source || this.translations.sourceSearch,
      });
    });
  }

  /**
   * @param {number} lat
   * @param {number} lng
   */
  centerMap(lat, lng) {
    if (!this.map || !this.marker) return;
    const position = { lat, lng };
    this.map.setCenter(position);
    this.map.setZoom(Math.max(this.defaultZoom, 15));
    this.marker.setPosition(position);
  }

  /**
   * @param {{address: string, lat: number, lng: number, placeId: string, source: string}} selection
   * @param {{skipMapUpdate?: boolean}} [options]
   */
  setPendingSelection(selection, options = {}) {
    this.pendingSelection = {
      address: selection.address,
      lat: selection.lat,
      lng: selection.lng,
      mapsUrl: `https://www.google.com/maps/search/?api=1&query=${selection.lat},${selection.lng}`,
      placeId: selection.placeId || '',
      source: selection.source,
      selectedAt: new Date().toISOString(),
    };

    if (this.autocomplete && selection.address) {
      this.autocomplete.value = selection.address;
    }

    this.setStatus('');
    this.updateProceedState();

    if (!options.skipMapUpdate) this.centerMap(selection.lat, selection.lng);
  }

  updateProceedState() {
    if (!this.proceedButton) return;
    this.proceedButton.disabled = !this.pendingSelection?.address;
  }

  /**
   * @param {MouseEvent} event
   */
  async handleProceedClick(event) {
    event.preventDefault();
    if (!this.pendingSelection?.address) return;

    this.savedSelection = this.pendingSelection;
    this.persistSelection(this.savedSelection);
    this.updateHeaderLabel(this.savedSelection);

    this.dispatchEvent(
      new CustomEvent('delivery-location:change', {
        bubbles: true,
        detail: { selection: this.savedSelection },
      })
    );

    // Callers awaiting `requestSelection()` (i.e. `ensureDeliveryLocation()`)
    // resume their own cart/add.js request as soon as this resolves `true` —
    // so the attribute sync must land on the cart FIRST, otherwise the
    // resumed Add to Cart races the sync and can complete (and render the
    // drawer/cart sections) before the address attributes are actually on
    // the cart, making them look blank until the next page load.
    await this.syncCartAttribute(this.savedSelection);
    this.resolvePending(true);

    this.closeDialog();
  }

  /**
   * Cart attribute keys written by earlier versions of this file that the
   * current version no longer writes: first a flat key per field, then (for
   * a short time) a single JSON-encoded `delivery-location` blob.
   * `/cart/update.js` only ever merges `attributes` in — it never drops a
   * key just because a later call omits it — so any cart that picked up
   * these keys under older code keeps carrying them (and forwarding them to
   * the order) forever unless something explicitly clears them. Shopify's
   * documented way to clear a cart attribute is to send it with an empty
   * string value, which is what `syncCartAttribute` below does for these on
   * every sync, alongside writing the current per-field attributes (see
   * `cartAttributeKeys`).
   */
  static legacyCartAttributeKeys = [
    'delivery-location',
    'delivery-location-address',
    'delivery-location-lat',
    'delivery-location-lng',
    'delivery-location-place-id',
    'delivery-location-map-link',
    'delivery-location-source',
    'delivery-location-selected-at',
  ];

  /**
   * Human-readable cart attribute keys, one per field, so each renders as
   * its own row in the Shopify admin cart/checkout/order "Additional
   * details" UI — that UI renders one line per attribute, but does not
   * honor embedded newlines within a single attribute's value, so a single
   * consolidated multi-line attribute collapses back onto one line there.
   */
  static cartAttributeKeys = {
    address: 'Delivery Address',
    lat: 'Latitude',
    lng: 'Longitude',
    mapsUrl: 'Google Maps Link',
  };

  /**
   * Writes the selection onto the cart as order-level attributes — the
   * authoritative copy of the delivery address, shared by every line item —
   * synced immediately on selection (and again on page load if a saved
   * selection is restored) rather than waiting for the next Add to Cart.
   * `/cart/update.js` merges the given `attributes` key into the cart's
   * existing attributes rather than replacing them wholesale, matching how
   * `cart-note.js` and `cart-discount.js` already use this endpoint. Also
   * clears any stale legacy attributes in the same request — see
   * `legacyCartAttributeKeys`.
   * @param {DeliveryLocationData} selection
   */
  async syncCartAttribute(selection) {
    try {
      /** @type {Record<string, string>} */
      const attributes = {
        [DeliveryLocationModal.cartAttributeKeys.address]: selection.address || '',
        [DeliveryLocationModal.cartAttributeKeys.lat]: selection.lat != null ? String(selection.lat) : '',
        [DeliveryLocationModal.cartAttributeKeys.lng]: selection.lng != null ? String(selection.lng) : '',
        [DeliveryLocationModal.cartAttributeKeys.mapsUrl]: selection.mapsUrl || '',
      };

      for (const key of DeliveryLocationModal.legacyCartAttributeKeys) {
        attributes[key] = '';
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({ attributes }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);
      if (!response.ok) return;

      const cart = await response.json();
      document.dispatchEvent(new CartUpdateEvent(cart, this.id || 'delivery-location-modal'));
    } catch {
      // Best-effort sync — the shopper's next explicit selection (or the next
      // page's restoreSelection) will retry.
    }
  }

  /**
   * @param {DeliveryLocationData} selection
   */
  persistSelection(selection) {
    try {
      window[this.storageType].setItem(this.storageKey, JSON.stringify(selection));
    } catch {
      // Storage unavailable (private browsing, quota exceeded, etc.) — selection
      // still works for the current page view via in-memory state.
    }
  }

  /**
   * Reflects the saved selection on every `[data-delivery-location-trigger]`
   * in the document (header trigger, hamburger drawer item — not assumed to
   * be singular) — a persistent, consistent "location set" confirmation via
   * a small pin-color change (see CSS in header-actions.liquid and
   * header-drawer.liquid), plus a hover/focus tooltip on the header trigger
   * ("Location is set" — static text, server-rendered directly in
   * header-actions.liquid, not the address itself).
   *
   * The trigger's own label is intentionally left alone (always "Delivery
   * location") rather than swapped to the address text — address length
   * varies a lot ("Delivery location" vs. a full multi-line address), and
   * swapping the label made the trigger's size/wrapping shift depending on
   * viewport and address length, which read as the "set" state being
   * inconsistent rather than a deliberate style change.
   *
   * The `--set` class toggle here is deliberately redundant with
   * `@theme/delivery-location-trigger-state`, which applies the same class
   * (and toggles the tooltip's `hidden` state) immediately on script load,
   * before this element even connects, since `<delivery-location-modal>`
   * renders near the end of `<body>` — see layout/theme.liquid. Both are
   * idempotent, so running both is harmless; this one exists so the state
   * still updates correctly on an in-page Proceed/Cancel, not just on
   * initial load.
   * @param {DeliveryLocationData | null} selection
   */
  updateHeaderLabel(selection) {
    const hasSelection = Boolean(selection?.address);

    for (const tooltip of document.querySelectorAll('[data-delivery-location-header-tooltip]')) {
      if (tooltip instanceof HTMLElement) {
        tooltip.hidden = !hasSelection;
      }
    }

    for (const trigger of document.querySelectorAll('[data-delivery-location-trigger]')) {
      trigger.classList.toggle('delivery-location-trigger--set', hasSelection);
    }
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  setStatus(message, isError = false) {
    if (!this.statusRegion) return;
    this.statusRegion.textContent = message;
    this.statusRegion.classList.toggle('delivery-location__status--error', isError);
  }

  get translations() {
    return {
      detectingLocation: this.dataset.tDetecting || '',
      errorPermissionDenied: this.dataset.tErrorPermission || '',
      errorPositionUnavailable: this.dataset.tErrorUnavailable || '',
      errorTimeout: this.dataset.tErrorTimeout || '',
      errorGeolocationUnsupported: this.dataset.tErrorUnsupported || '',
      errorGeocodeFailed: this.dataset.tErrorGeocode || '',
      errorInvalidAddress: this.dataset.tErrorInvalid || '',
      errorApiLoadFailed: this.dataset.tErrorApiLoad || '',
      errorApiKeyMissing: this.dataset.tErrorApiKey || '',
      sourceCurrentLocation: this.dataset.tSourceCurrent || '',
      sourceSearch: this.dataset.tSourceSearch || '',
      headerTriggerDefault: this.dataset.tHeaderTriggerDefault || '',
    };
  }
}

if (!customElements.get('delivery-location-modal')) {
  customElements.define('delivery-location-modal', DeliveryLocationModal);
}
